import { useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  CheckCircle2, XCircle, Loader2, Wallet, Paperclip, Clock, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/DatePicker";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const METHOD_LABEL = {
  bank_transfer: "Bank transfer", cash: "Cash", card: "Card", cheque: "Cheque", other: "Other",
};

/** Public (no-auth) page — a client tells KINOS they have paid, with proof. */
export default function PayReport() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const preselectInvoice = searchParams.get("invoice") || "";
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["public-pay", token],
    queryFn: () => api.getPublicPay(token),
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <div className="max-w-md text-center space-y-3" data-testid="pay-invalid">
          <XCircle className="size-10 text-red-500 mx-auto" />
          <h1 className="text-xl font-bold">Link not found</h1>
          <p className="text-sm text-muted-foreground">
            This payment link is invalid or has expired. Please contact KINOS at{" "}
            <a href="mailto:info@kinos.tl" className="text-primary underline">info@kinos.tl</a>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="pay-page">
      <Toaster position="top-center" richColors />
      <header className="bg-white border-b border-border">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center gap-2">
          <Wallet className="size-5 text-primary" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
              KINOS — Leading Innovation Technology
            </div>
            <div className="font-semibold text-sm">Report a payment</div>
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-8 space-y-6">
        <section className="bg-white border border-border rounded-md p-5">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Account</div>
          <div className="text-lg font-bold" data-testid="pay-client-name">{data.client.name}</div>
          <p className="text-xs text-muted-foreground mt-1">
            Select the invoices you have settled, tell us the amount and date, and attach your transfer receipt.
            Our finance team will confirm and send you an updated receipt.
          </p>
        </section>
        {data.pending_claims.length > 0 && <PendingList claims={data.pending_claims} />}
        {data.invoices.length === 0 ? (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-md p-5 flex items-center gap-2"
               data-testid="pay-nothing-outstanding">
            <CheckCircle2 className="size-5" /> Your account has no outstanding invoices. Thank you!
          </div>
        ) : (
          <ClaimForm token={token} invoices={data.invoices} methods={data.methods} onDone={refetch} preselectInvoice={preselectInvoice} />
        )}
      </main>
    </div>
  );
}

function PendingList({ claims }) {
  return (
    <section className="bg-amber-50 border border-amber-200 rounded-md p-4" data-testid="pay-pending-list">
      <div className="flex items-center gap-2 font-semibold text-amber-900 text-sm mb-2">
        <Clock className="size-4" /> Awaiting confirmation by KINOS
      </div>
      <ul className="text-xs text-amber-900/90 space-y-1">
        {claims.map((c) => (
          <li key={c.id} data-testid={`pay-pending-${c.id}`}>
            {fmtDate(c.paid_on)} — <b>{fmtMoney(c.amount)} {c.currency}</b> for{" "}
            {c.invoices.map((i) => i.invoice_number).join(", ")}
            {c.reference ? ` · ref ${c.reference}` : ""}{c.has_proof ? " · proof attached" : ""}
          </li>
        ))}
      </ul>
    </section>
  );
}

function ClaimForm({ token, invoices, methods, onDone, preselectInvoice = "" }) {
  const [selected, setSelected] = useState(() => {
    if (preselectInvoice && invoices.some((i) => i.id === preselectInvoice)) return new Set([preselectInvoice]);
    const overdue = invoices.filter((i) => i.overdue_days > 0);
    return new Set(overdue.length <= 10 ? overdue.map((i) => i.id) : []);
  });
  const [amount, setAmount] = useState("");
  const [amountTouched, setAmountTouched] = useState(false);
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState("bank_transfer");
  const [reference, setReference] = useState("");
  const [reporter, setReporter] = useState("");
  const [reporterEmail, setReporterEmail] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [done, setDone] = useState(null);
  const fileRef = useRef(null);

  const chosen = invoices.filter((i) => selected.has(i.id));
  const selectedTotal = useMemo(
    () => chosen.reduce((s, i) => s + i.balance_due, 0), [chosen],
  );
  const currency = chosen[0]?.currency || invoices[0]?.currency || "USD";
  const mixed = new Set(chosen.map((i) => i.currency)).size > 1;
  const effectiveAmount = amountTouched ? amount : selectedTotal.toFixed(2);

  function toggle(id) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }

  const mut = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append("invoice_ids", JSON.stringify([...selected]));
      fd.append("amount", effectiveAmount);
      fd.append("paid_on", paidOn);
      fd.append("method", method);
      fd.append("reference", reference.trim());
      fd.append("reporter_name", reporter.trim());
      fd.append("reporter_email", reporterEmail.trim());
      fd.append("note", note.trim());
      if (file) fd.append("file", file);
      return api.submitPublicPayClaim(token, fd);
    },
    onSuccess: (res) => {
      setDone(res.claim);
      toast.success("Thank you — payment reported");
      onDone();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Something went wrong"),
  });

  if (done) {
    return (
      <section className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-md p-5"
               data-testid="pay-success">
        <div className="flex items-center gap-2 font-semibold">
          <CheckCircle2 className="size-5" /> Payment reported — thank you!
        </div>
        <p className="text-xs mt-1.5 opacity-80">
          We recorded your report of <b>{fmtMoney(done.amount)} {done.currency}</b> paid on {fmtDate(done.paid_on)} for{" "}
          {done.invoices.map((i) => i.invoice_number).join(", ")}. Finance will confirm shortly and email you a receipt.
        </p>
        <Button variant="outline" size="sm" className="mt-3 bg-white" data-testid="pay-report-another"
                onClick={() => { setDone(null); setSelected(new Set()); setAmountTouched(false); setFile(null); }}>
          Report another payment
        </Button>
      </section>
    );
  }

  const canSubmit = selected.size > 0 && !mixed && Number(effectiveAmount) > 0 && paidOn && !mut.isPending;

  return (
    <section className="bg-white border border-border rounded-md p-5 space-y-5" data-testid="pay-form">
      <div>
        <Label className="overline">1 · Which invoices did you pay?</Label>
        <div className="mt-2 border border-border rounded-md divide-y divide-border max-h-80 overflow-y-auto" data-testid="pay-invoice-list">
          {invoices.map((inv) => (
            <label key={inv.id} className="flex items-center gap-3 px-3 py-2.5 text-sm cursor-pointer hover:bg-slate-50">
              <input type="checkbox" className="size-4 accent-[#C8102E]"
                     data-testid={`pay-inv-${inv.id}`}
                     checked={selected.has(inv.id)} onChange={() => toggle(inv.id)} />
              <span className="font-mono text-xs w-36 shrink-0">{inv.invoice_number}</span>
              <span className="text-xs text-muted-foreground flex-1">
                due {fmtDate(inv.due_date) || "—"}
                {inv.overdue_days > 0 && (
                  <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-700 ring-1 ring-inset ring-red-200">
                    {inv.overdue_days}d overdue
                  </span>
                )}
              </span>
              <span className="font-mono text-sm font-semibold">{fmtMoney(inv.balance_due)} {inv.currency}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-between text-xs mt-2 text-muted-foreground">
          <span className="flex items-center gap-2">
            {selected.size} selected
            {selected.size > 0 && (
              <button type="button" className="underline" data-testid="pay-clear-selection"
                      onClick={() => { setSelected(new Set()); setAmountTouched(false); }}>
                Clear
              </button>
            )}
          </span>
          <span data-testid="pay-selected-total">Selected balance: <b className="font-mono">{fmtMoney(selectedTotal)} {currency}</b></span>
        </div>
        {mixed && <p className="text-xs text-red-600 mt-1">Please report invoices in one currency at a time.</p>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="overline">2 · Amount paid ({currency}) *</Label>
          <Input data-testid="pay-amount" type="number" min="0" step="0.01" value={effectiveAmount}
                 onChange={(e) => { setAmountTouched(true); setAmount(e.target.value); }} />
        </div>
        <div>
          <Label className="overline">Date paid *</Label>
          <DatePicker testId="pay-date" value={paidOn} onChange={setPaidOn} disabled={{ after: new Date() }} />
        </div>
        <div>
          <Label className="overline">Method</Label>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              {methods.map((m) => <SelectItem key={m} value={m}>{METHOD_LABEL[m] || m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline">Reference / transaction no.</Label>
          <Input data-testid="pay-reference" value={reference} onChange={(e) => setReference(e.target.value)}
                 placeholder="e.g. BNU 2026-07-114523" />
        </div>
        <div>
          <Label className="overline">Your name</Label>
          <Input data-testid="pay-reporter" value={reporter} onChange={(e) => setReporter(e.target.value)} />
        </div>
        <div>
          <Label className="overline">Your email (for confirmation)</Label>
          <Input data-testid="pay-reporter-email" type="email" value={reporterEmail}
                 onChange={(e) => setReporterEmail(e.target.value)} placeholder="you@company.com" />
        </div>
        <div>
          <Label className="overline">3 · Proof of payment (image or PDF)</Label>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden"
                 data-testid="pay-proof-input"
                 onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ""; }} />
          <div className="flex items-center gap-2 mt-1">
            <Button type="button" variant="outline" size="sm" data-testid="pay-proof-btn"
                    onClick={() => fileRef.current?.click()}>
              <Paperclip className="size-3.5 mr-1.5" /> {file ? "Replace file" : "Attach receipt"}
            </Button>
            {file && (
              <span className="text-xs text-muted-foreground flex items-center gap-1" data-testid="pay-proof-name">
                {file.name}
                <button type="button" className="text-red-600" onClick={() => setFile(null)} aria-label="Remove file">
                  <Trash2 className="size-3.5" />
                </button>
              </span>
            )}
          </div>
        </div>
        <div className="sm:col-span-2">
          <Label className="overline">Note to KINOS</Label>
          <Textarea data-testid="pay-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      </div>

      <Button data-testid="pay-submit" className="w-full sm:w-auto bg-primary hover:bg-primary/90"
              disabled={!canSubmit} onClick={() => mut.mutate()}>
        {mut.isPending ? "Sending…" : "Send payment report"}
      </Button>
    </section>
  );
}
